import { Typography } from '@mui/material';
import { useFormActions } from '@ngt/forms-core';
import {
    CollaboratingGroupContext,
    FileUpload,
    FormOptionsContext,
    GroupedFields,
    GroupedTableArrayField,
    IFileUpload,
    IGroupedTableArrayFieldColumn,
    Input,
    InstitutionContext,
    MasterGroupContext,
    PatientContext,
    Select,
    Text,
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { ComponentType, FC, useCallback, useContext, useMemo } from 'react';
import { File, SourceDocumentForm } from '@/api/dtos';

const existingLink = (value?: IFileUpload) =>
    `/opms/file/view/${(value as File)?.id}`;

const existingDownloadLink = (value?: IFileUpload) =>
    `/opms/file/download/${(value as File)?.id}`;

interface SourceDocumentsProps {
    fileUploadView?: ComponentType<any>;
    viewProps?: Record<string, any>;
}

const SourceDocuments: FC<SourceDocumentsProps> = ({
    fileUploadView,
    viewProps,
}) => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);
    const { readOnly } = useContext(FormOptionsContext);
    const { setFieldValue, submit } = useFormActions();

    const {
        permissions: [canAdministerOpms],
    } = usePermissionsByIds(
        [Permission.OpmsAdminister],
        masterGroup?.id,
        collaboratingGroup?.id,
        institution?.id,
        patient?.id
    );

    const onFileUploadChange = useCallback((value?: IFileUpload) => {
        if (readOnly && value?.upload != null) {
            setFieldValue('submitType', 'save');
            submit();
        }
    }, [readOnly, setFieldValue, submit]);

    const columns = useMemo<Array<IGroupedTableArrayFieldColumn<SourceDocumentForm>>>(() => [
        {
            propertyPath: 'name',
            content: <Input component={Text} />,
        },
        {
            propertyPath: 'type',
            content: <Input component={Select} />,
        },
        {
            propertyPath: 'file',
            content: (
                <Input
                    component={FileUpload}
                    existingLink={existingLink}
                    existingDownloadLink={existingDownloadLink}
                    view={fileUploadView}
                    ViewProps={viewProps}
                    allowRedactOnFormDisable={canAdministerOpms ?? false}
                    onChange={onFileUploadChange}
                />
            ),
        },
    ], [canAdministerOpms, fileUploadView, onFileUploadChange, viewProps]);

    return (
        <GroupedFields>
            <Typography variant="h2" sx={{ px: 2, pt: 3, pb: 1 }}>
                Source Documents
            </Typography>
            <GroupedTableArrayField<SourceDocumentForm>
                entityName="Document"
                propertyPath="sourceDocuments"
                columns={columns}
                AddButtonProps={{ sx: { mb: 2 } }}
            />
        </GroupedFields>
    );
};

export default SourceDocuments;
